import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { projectsAPI, filesAPI } from '../api';
import { useAuth } from '../AuthContext';
import ProjectFeed from '../components/ProjectFeed';
import MarkdownContent from '../components/MarkdownContent';

function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [message, setMessage] = useState('');
  const [showCollabModal, setShowCollabModal] = useState(false);
  const [collabInput, setCollabInput] = useState('');
  const [collabLoading, setCollabLoading] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [showAddFileModal, setShowAddFileModal] = useState(false);
  const [myFiles, setMyFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [screenshots, setScreenshots] = useState([]);
  const [videos, setVideos] = useState([]);
  const [showAddScreenshotModal, setShowAddScreenshotModal] = useState(false);
  const [showAddVideoModal, setShowAddVideoModal] = useState(false);
  const [screenshotData, setScreenshotData] = useState({ url: '', caption: '', file: null });
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'url'
  const [videoData, setVideoData] = useState({ url: '', title: '', type: 'youtube' });
  const [mediaLoading, setMediaLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('files');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [iconUploading, setIconUploading] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferUsername, setTransferUsername] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [salesData, setSalesData] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [wallets, setWallets] = useState([]);
  const [walletLoading, setWalletLoading] = useState(false);
  const [showSeedModal, setShowSeedModal] = useState(false);
  const [seedPhrase, setSeedPhrase] = useState('');

  useEffect(() => {
    loadProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (project) {
      loadSales();
      loadWallets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, user]);

  const loadProject = async () => {
    setLoading(true);
    try {
      const response = await projectsAPI.get(projectId);
      if (response.data.success) {
        const proj = response.data.project;
        setProject(proj);
        setFormData({
          title: proj.title,
          short_description: proj.short_description || '',
          description: proj.description || '',
          is_public: proj.is_public,
          pricing_type: proj.pricing_type || 'pwyw',
          price: proj.price || '',
          currency: proj.currency || 'USD',
          crypto_addresses: proj.crypto_addresses || {},
          privacy_policy: proj.privacy_policy || '',
          terms_of_use: proj.terms_of_use || ''
        });
        setScreenshots(proj.screenshots || []);
        setVideos(proj.videos || []);
        // Load collaborators after project is loaded
        loadCollaborators();
      }
    } catch (error) {
      console.error('Error loading project:', error);
      setMessage('Project not found');
    } finally {
      setLoading(false);
    }
  };

  const loadCollaborators = async () => {
    try {
      const response = await projectsAPI.listCollaborators(projectId);
      if (response.data.success) {
        setCollaborators(response.data.collaborators || []);
      }
    } catch (error) {
      // Silently fail - might not have permission or project might be public
      console.log('Could not load collaborators:', error.response?.status);
    }
  };

  const loadSales = async () => {
    if (!user || !project || project.owner !== user.username) return;
    setSalesLoading(true);
    try {
      const response = await projectsAPI.getSales(projectId);
      if (response.data.success) {
        setSalesData(response.data.sales);
      }
    } catch (error) {
      console.log('Could not load sales:', error.response?.status);
    } finally {
      setSalesLoading(false);
    }
  };

  const loadWallets = async () => {
    if (!user || !project || project.owner !== user.username) return;
    setWalletLoading(true);
    try {
      const response = await projectsAPI.getWallets(projectId);
      if (response.data.success) {
        setWallets(response.data.wallets || []);
      }
    } catch (error) {
      console.log('Could not load wallets:', error.response?.status);
    } finally {
      setWalletLoading(false);
    }
  };

  const handleGenerateWallet = async (chain) => {
    try {
      const response = await projectsAPI.generateWallet(projectId, chain);
      if (response.data.success) {
        setSeedPhrase(response.data.seed_phrase);
        setShowSeedModal(true);
        loadWallets();
        setMessage(`Wallet generated for ${chain.toUpperCase()}`);
      }
    } catch (error) {
      setMessage(error.response?.data?.error || `Failed to generate ${chain} wallet`);
    }
  };

  const handleViewSeed = async (chain) => {
    try {
      const response = await projectsAPI.getWalletSeed(projectId, chain);
      if (response.data.success) {
        setSeedPhrase(response.data.seed_phrase);
        setShowSeedModal(true);
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to retrieve seed phrase');
    }
  };

  const handleAddCollaborator = async (e) => {
    e.preventDefault();
    if (!collabInput.trim()) return;

    setCollabLoading(true);
    setMessage('');

    try {
      const data = collabInput.includes('@')
        ? { email: collabInput.trim() }
        : { username: collabInput.trim() };

      const response = await projectsAPI.addCollaborator(projectId, data);
      if (response.data.success) {
        setMessage('Collaborator added successfully!');
        setCollabInput('');
        setShowCollabModal(false);
        loadCollaborators();
        loadProject();
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to add collaborator');
    } finally {
      setCollabLoading(false);
    }
  };

  const handleRemoveCollaborator = async (username) => {
    if (!window.confirm(`Remove ${username} from this project?`)) return;

    try {
      const response = await projectsAPI.removeCollaborator(projectId, username);
      if (response.data.success) {
        setMessage('Collaborator removed successfully!');
        loadCollaborators();
        loadProject();
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to remove collaborator');
    }
  };

  const handleTransferOwnership = async (e) => {
    e.preventDefault();
    
    if (!transferUsername.trim()) {
      setMessage('Please enter a username');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to transfer ownership of this project to ${transferUsername}? This action cannot be undone.`)) {
      return;
    }
    
    setTransferLoading(true);
    try {
      const response = await projectsAPI.transferOwnership(projectId, transferUsername);
      if (response.data.success) {
        setMessage(`Project ownership transferred to ${transferUsername}. You are now a collaborator.`);
        setShowTransferModal(false);
        setTransferUsername('');
        loadProject();
        loadCollaborators();
        // Redirect after a short delay
        setTimeout(() => {
          navigate('/projects');
        }, 2000);
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to transfer ownership');
    } finally {
      setTransferLoading(false);
    }
  };

  const loadMyFiles = async () => {
    setFilesLoading(true);
    try {
      const response = await filesAPI.getMyFiles();
      if (response.data.success) {
        // Filter out files already in the project
        const existingFileIds = project.downloads.map(d => d.file_id);
        const availableFiles = response.data.files.filter(
          file => !existingFileIds.includes(file.file_id)
        );
        setMyFiles(availableFiles);
      }
    } catch (error) {
      console.error('Error loading files:', error);
    } finally {
      setFilesLoading(false);
    }
  };

  const handleAddFilesClick = () => {
    setShowAddFileModal(true);
    setSelectedFiles([]);
    loadMyFiles();
  };

  const toggleFileSelection = (fileId) => {
    setSelectedFiles(prev => {
      if (prev.includes(fileId)) {
        return prev.filter(id => id !== fileId);
      } else {
        return [...prev, fileId];
      }
    });
  };

  const handleAddSelectedFiles = async () => {
    if (selectedFiles.length === 0) {
      setMessage('Please select at least one file');
      return;
    }

    setFilesLoading(true);
    try {
      // Add each selected file to the project
      for (const fileId of selectedFiles) {
        await projectsAPI.addDownload(projectId, { file_id: fileId });
      }
      
      setMessage(`Successfully added ${selectedFiles.length} file(s) to project`);
      setShowAddFileModal(false);
      setSelectedFiles([]);
      loadProject();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error adding files:', error);
      setMessage(error.response?.data?.error || 'Failed to add files');
    } finally {
      setFilesLoading(false);
    }
  };

  const handleRemoveFile = async (fileId) => {
    if (!window.confirm('Remove this file from the project?')) return;
    
    try {
      await projectsAPI.removeDownload(projectId, fileId);
      setMessage('File removed from project');
      loadProject();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error removing file:', error);
      setMessage(error.response?.data?.error || 'Failed to remove file');
    }
  };

  const handleToggleFree = async (fileId, currentIsFree) => {
    try {
      await projectsAPI.updateDownload(projectId, fileId, { is_free: !currentIsFree });
      setMessage(!currentIsFree ? 'File marked as free' : 'File marked as paid');
      loadProject();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating file:', error);
      setMessage(error.response?.data?.error || 'Failed to update file');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setMessage('');

    try {
      const response = await projectsAPI.update(projectId, formData);
      if (response.data.success) {
        setProject(response.data.project);
        setEditing(false);
        setMessage('Project updated successfully!');
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to update project');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this project? Files will NOT be deleted.')) return;
    
    try {
      await projectsAPI.delete(projectId);
      navigate('/projects');
    } catch (error) {
      setMessage('Failed to delete project');
    }
  };

  const copyPublicUrl = () => {
    const url = projectsAPI.getPublicUrl(project.slug);
    navigator.clipboard.writeText(url);
    setMessage('Public URL copied to clipboard!');
  };

  // Icon handling functions
  const handleIconFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      validateAndSetIconFile(file);
    }
  };

  const validateAndSetIconFile = (file) => {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setMessage('Invalid file type. Please use PNG, JPG, GIF, or WebP');
      return false;
    }
    if (file.size > 2 * 1024 * 1024) {
      setMessage('Icon too large. Maximum size is 2MB');
      return false;
    }
    setIconFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setIconPreview(reader.result);
    };
    reader.readAsDataURL(file);
    return true;
  };

  const handleIconUpload = async () => {
    if (!iconFile) return;
    
    setIconUploading(true);
    try {
      const response = await projectsAPI.uploadIcon(projectId, iconFile);
      if (response.data.success) {
        setMessage('Icon uploaded successfully!');
        setIconFile(null);
        setIconPreview(null);
        loadProject();
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to upload icon');
    } finally {
      setIconUploading(false);
    }
  };

  const handleDeleteIcon = async () => {
    if (!window.confirm('Remove project icon?')) return;
    
    try {
      await projectsAPI.deleteIcon(projectId);
      setMessage('Icon removed');
      loadProject();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to remove icon');
    }
  };

  const getIconUrl = (iconPath) => {
    if (!iconPath) return null;
    if (iconPath.startsWith('/api/')) {
      const API_URL = process.env.REACT_APP_API_URL || '';
      // Remove '/api' prefix (4 chars) since API_URL already ends with /api
      return API_URL + iconPath.substring(4);
    }
    return iconPath;
  };

  const validateAndSetScreenshotFile = (file) => {
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setMessage('Invalid file type. Please use PNG, JPG, GIF, or WebP');
      return false;
    }
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setMessage('File too large. Maximum size is 10MB');
      return false;
    }
    setScreenshotData(prev => ({ ...prev, file }));
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setScreenshotPreview(reader.result);
    };
    reader.readAsDataURL(file);
    return true;
  };

  const handleScreenshotFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      validateAndSetScreenshotFile(file);
    }
  };

  const handleScreenshotPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          // Generate a filename for pasted images
          const extension = file.type.split('/')[1] || 'png';
          const pastedFile = new File([file], `pasted-screenshot.${extension}`, { type: file.type });
          validateAndSetScreenshotFile(pastedFile);
          setUploadMode('file'); // Switch to file mode when pasting
        }
        break;
      }
    }
  };

  const handleScreenshotDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        validateAndSetScreenshotFile(file);
      } else {
        setMessage('Please drop an image file');
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const resetScreenshotModal = () => {
    setScreenshotData({ url: '', caption: '', file: null });
    setScreenshotPreview(null);
    setUploadMode('file');
    setShowAddScreenshotModal(false);
  };

  const handleAddScreenshot = async (e) => {
    e.preventDefault();
    
    if (uploadMode === 'file') {
      if (!screenshotData.file) {
        setMessage('Please select a screenshot file');
        return;
      }
    } else {
      if (!screenshotData.url.trim()) {
        setMessage('Please enter a screenshot URL');
        return;
      }
    }

    setMediaLoading(true);
    setMessage('');

    try {
      if (uploadMode === 'file') {
        await projectsAPI.uploadScreenshot(projectId, screenshotData.file, screenshotData.caption);
      } else {
        await projectsAPI.addScreenshot(projectId, { url: screenshotData.url, caption: screenshotData.caption });
      }
      setMessage('Screenshot added successfully!');
      resetScreenshotModal();
      loadProject();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to add screenshot');
    } finally {
      setMediaLoading(false);
    }
  };

  const handleRemoveScreenshot = async (screenshotId) => {
    if (!window.confirm('Remove this screenshot?')) return;

    try {
      await projectsAPI.removeScreenshot(projectId, screenshotId);
      setMessage('Screenshot removed successfully!');
      loadProject();
    } catch (error) {
      setMessage('Failed to remove screenshot');
    }
  };

  const handleAddVideo = async (e) => {
    e.preventDefault();
    if (!videoData.url.trim()) {
      setMessage('Please enter a video URL');
      return;
    }

    setMediaLoading(true);
    setMessage('');

    try {
      await projectsAPI.addVideo(projectId, videoData);
      setMessage('Video added successfully!');
      setVideoData({ url: '', title: '', type: 'youtube' });
      setShowAddVideoModal(false);
      loadProject();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to add video');
    } finally {
      setMediaLoading(false);
    }
  };

  const handleRemoveVideo = async (videoId) => {
    if (!window.confirm('Remove this video?')) return;

    try {
      await projectsAPI.removeVideo(projectId, videoId);
      setMessage('Video removed successfully!');
      loadProject();
    } catch (error) {
      setMessage('Failed to remove video');
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container">
        <div className="card">
          <h2>Project not found</h2>
          <Link to="/projects" className="btn btn-primary mt-3">
            ← Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  const isOwner = user?.username === project.owner;
  const canManageFiles = isOwner || project.collaborators?.includes(user?.username);

  return (
    <div className="container">
      {/* Header */}
      <div className="d-flex justify-between align-center mb-3">
        <div>
          <Link to="/projects" className="back-link">
            ← Back to Projects
          </Link>
          <h1 className="mt-2">{project.title}</h1>
        </div>
        {isOwner && (
          <div className="d-flex gap-2">
            <button
              onClick={() => setEditing(!editing)}
              className="btn btn-secondary"
            >
              {editing ? 'Cancel' : 'Edit'}
            </button>
            <button
              onClick={() => setShowTransferModal(true)}
              className="btn btn-warning"
            >
              Transfer Ownership
            </button>
            <button
              onClick={handleDelete}
              className="btn btn-danger"
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {message && (
        <div className={`alert ${message.includes('success') || message.includes('copied') ? 'alert-success' : 'alert-error'}`}>
          {message}
        </div>
      )}

      {/* Edit Form or Display */}
      {editing ? (
        <div className="card mb-3">
          <h3 className="mb-3">Edit Project</h3>
          <form onSubmit={handleUpdate}>
            {/* Project Icon */}
            <div className="form-group">
              <label className="form-label">Project Icon</label>
              <div className="icon-upload-row">
                <div className="icon-preview-box">
                  {iconPreview ? (
                    <img src={iconPreview} alt="Icon preview" className="icon-preview-image" />
                  ) : project.thumbnail ? (
                    <img src={getIconUrl(project.thumbnail)} alt="Current icon" className="icon-preview-image" />
                  ) : (
                    <div className="icon-preview-placeholder">
                      {project.title.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="icon-upload-info">
                  <div className="icon-upload-buttons">
                    <input
                      type="file"
                      id="icon-upload"
                      accept="image/png,image/jpeg,image/gif,image/webp"
                      onChange={handleIconFileChange}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="icon-upload" className="btn btn-secondary btn-sm">
                      {project.thumbnail ? 'Change' : 'Choose File'}
                    </label>
                    {iconFile && (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleIconUpload}
                        disabled={iconUploading}
                      >
                        {iconUploading ? 'Uploading...' : 'Upload'}
                      </button>
                    )}
                    {project.thumbnail && !iconFile && (
                      <button
                        type="button"
                        className="btn-icon-delete"
                        onClick={handleDeleteIcon}
                        title="Remove icon"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                        </svg>
                      </button>
                    )}
                  </div>
                  <span className="icon-upload-hint">PNG, JPG, GIF or WebP. Max 2MB.</span>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Title *</label>
              <input
                type="text"
                className="form-control"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Short Description</label>
              <input
                type="text"
                className="form-control"
                value={formData.short_description}
                onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                maxLength={100}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={5}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Privacy Policy</label>
              <textarea
                className="form-control"
                value={formData.privacy_policy}
                onChange={(e) => setFormData({ ...formData, privacy_policy: e.target.value })}
                placeholder="Paste your project's privacy policy here..."
                rows={5}
              />
              <span className="form-hint">This will be hosted at your project's public privacy policy page.</span>
            </div>

            <div className="form-group">
              <label className="form-label">Terms of Use</label>
              <textarea
                className="form-control"
                value={formData.terms_of_use}
                onChange={(e) => setFormData({ ...formData, terms_of_use: e.target.value })}
                placeholder="Paste your project's terms of use here..."
                rows={5}
              />
              <span className="form-hint">This will be hosted at your project's public terms of use page.</span>
            </div>

            <div className="form-group">
              <label className="form-label">Pricing</label>
              <select
                className="form-control"
                value={formData.pricing_type}
                onChange={(e) => setFormData({ ...formData, pricing_type: e.target.value })}
              >
                <option value="free">Free</option>
                <option value="fixed">Fixed Price</option>
                <option value="pwyw">Pay What You Want</option>
              </select>
            </div>

            {formData.pricing_type !== 'free' && (
              <>
                <div className="form-group">
                  <label className="form-label">Price</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="e.g. 10.00"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Currency</label>
                  <select
                    className="form-control"
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="BTC">BTC</option>
                    <option value="USDT">USDT</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">BTC Address</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.crypto_addresses?.btc || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      crypto_addresses: { ...formData.crypto_addresses, btc: e.target.value }
                    })}
                    placeholder="bc1..."
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">USDT (TRC20) Address</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.crypto_addresses?.usdt_trc20 || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      crypto_addresses: { ...formData.crypto_addresses, usdt_trc20: e.target.value }
                    })}
                    placeholder="T..."
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">USDT (BEP20) Address</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.crypto_addresses?.usdt_bep20 || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      crypto_addresses: { ...formData.crypto_addresses, usdt_bep20: e.target.value }
                    })}
                    placeholder="0x..."
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">ETH / BASE Address</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.crypto_addresses?.eth || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      crypto_addresses: { ...formData.crypto_addresses, eth: e.target.value }
                    })}
                    placeholder="0x..."
                  />
                </div>
              </>
            )}

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.is_public}
                  onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                  className="checkbox-input"
                />
                <span className="checkbox-text">
                  <span className="checkbox-title">Public Project</span>
                  <span className="checkbox-description">Anyone can view and download</span>
                </span>
              </label>
            </div>

            <div className="d-flex gap-2">
              <button type="submit" className="btn btn-primary">
                Save Changes
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setEditing(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="card mb-3">
          <div className="project-header-with-icon">
            <div className="project-icon-display">
              {project.thumbnail ? (
                <img src={getIconUrl(project.thumbnail)} alt={project.title} className="project-icon-image" />
              ) : (
                <div className="project-icon-placeholder">
                  {project.title.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div className="project-header-text">
              <h2>{project.title}</h2>
              {project.short_description && (
                <p className="project-tagline">{project.short_description}</p>
              )}
            </div>
          </div>
          {project.description && (
            <div className="project-description">
              <MarkdownContent content={project.description} />
            </div>
          )}
          <div className="mt-3">
            <span className={`badge ${project.is_public ? 'badge-success' : 'badge-warning'}`}>
              {project.is_public ? 'Public' : 'Private'}
            </span>
          </div>
          {(project.privacy_policy || project.terms_of_use) && (
            <div className="mt-2 d-flex gap-2" style={{ flexWrap: 'wrap' }}>
              {project.privacy_policy && (
                <Link
                  to={`/p/${project.slug}/privacy`}
                  className="text-small sidebar-link"
                >
                  Privacy Policy
                </Link>
              )}
              {project.terms_of_use && (
                <Link
                  to={`/p/${project.slug}/terms`}
                  className="text-small sidebar-link"
                >
                  Terms of Use
                </Link>
              )}
            </div>
          )}
        </div>
      )}

      {/* Stats Bar - Always Visible */}
      <div className="stats-bar mb-3">
        <div className="stat-item">
          <span className="stat-value">{project.stats.total_downloads}</span>
          <span className="stat-label">Downloads</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{project.stats.views}</span>
          <span className="stat-label">Views</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{project.downloads.length}</span>
          <span className="stat-label">Files</span>
        </div>
      </div>

      {/* Public URL - Always Visible if Public */}
      {project.is_public && (
        <div className="public-url-bar mb-3">
          <span className="public-url-label">Public URL:</span>
          <input
            type="text"
            className="public-url-input"
            value={projectsAPI.getPublicUrl(project.slug)}
            readOnly
          />
          <button onClick={copyPublicUrl} className="btn btn-primary btn-sm">
            📋 Copy
          </button>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="project-tabs">
        <div className="tab-nav">
          <button 
            className={`tab-btn ${activeTab === 'files' ? 'active' : ''}`}
            onClick={() => setActiveTab('files')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>
            Files ({project.downloads.length})
          </button>
          <button 
            className={`tab-btn ${activeTab === 'media' ? 'active' : ''}`}
            onClick={() => setActiveTab('media')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>
            Media ({screenshots.length + videos.length})
          </button>
          <button 
            className={`tab-btn ${activeTab === 'collaborators' ? 'active' : ''}`}
            onClick={() => setActiveTab('collaborators')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
            Collaborators ({collaborators.length})
          </button>
          <button 
            className={`tab-btn ${activeTab === 'updates' ? 'active' : ''}`}
            onClick={() => setActiveTab('updates')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path></svg>
            Updates
          </button>
          <button 
            className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
            Merchant
          </button>
        </div>

        <div className="tab-content">
          {/* Files Tab */}
          {activeTab === 'files' && (
            <div className="tab-pane">
              <div className="d-flex justify-between align-center mb-3">
                <h3>Files</h3>
                {canManageFiles && (
                  <button onClick={handleAddFilesClick} className="btn btn-primary btn-sm">
                    + Link Files
                  </button>
                )}
              </div>
              
              {project.downloads.length === 0 ? (
                <div className="empty-state">
                  <h4>No files yet</h4>
                  <p className="text-muted">
                    Link your uploaded files to this project
                  </p>
                  {canManageFiles && (
                    <div className="d-flex gap-2 mt-2">
                      <button onClick={handleAddFilesClick} className="btn btn-primary">
                        Link Existing Files
                      </button>
                      <Link to="/my-files" className="btn btn-secondary">
                        Upload New Files
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>Version</th>
                      <th>Platform</th>
                      <th>Size</th>
                      <th>Downloads</th>
                      <th>Added</th>
                      <th>Access</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {project.downloads.map((file) => (
                      <tr key={file.file_id}>
                        <td>
                          <div className="file-info">
                            <span className="file-name">{file.filename}</span>
                          </div>
                        </td>
                        <td>
                          <span className="badge badge-info">
                            {file.version || 'N/A'}
                          </span>
                        </td>
                        <td>{file.platform || 'N/A'}</td>
                        <td>{formatBytes(file.size)}</td>
                        <td className="download-count">{file.downloads || 0}</td>
                        <td className="text-small text-muted">{formatDate(file.added_at)}</td>
                        <td>
                          {file.is_free ? (
                            <span className="badge badge-success" title="Anyone can download this file">Free</span>
                          ) : (
                            <span className="badge badge-warning" title="Requires purchase">Paid</span>
                          )}
                          {canManageFiles && project.pricing_type !== 'free' && (
                            <button
                              onClick={() => handleToggleFree(file.file_id, !!file.is_free)}
                              className="btn btn-sm btn-secondary ml-2"
                              title={file.is_free ? 'Mark as paid' : 'Mark as free'}
                              style={{marginLeft: '0.5rem'}}
                            >
                              {file.is_free ? 'Make Paid' : 'Make Free'}
                            </button>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <a href={projectsAPI.downloadProjectFile(projectId, file.file_id)} className="btn btn-primary btn-sm" title="Download file">
                              Download
                            </a>
                            {canManageFiles && (
                              <button
                                onClick={() => handleRemoveFile(file.file_id)}
                                className="btn btn-danger btn-sm"
                                title="Remove from project"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Media Tab (Screenshots + Videos) */}
          {activeTab === 'media' && (
            <div className="tab-pane">
              {/* Screenshots Section */}
              <div className="media-section">
                <div className="d-flex justify-between align-center mb-3">
                  <h3>Screenshots ({screenshots.length})</h3>
                  <button onClick={() => setShowAddScreenshotModal(true)} className="btn btn-primary btn-sm">
                    + Add Screenshot
                  </button>
                </div>
                
                {screenshots.length === 0 ? (
                  <div className="empty-state">
                    <h4>No screenshots yet</h4>
                    <p className="text-muted">
                      Add screenshots to showcase your project
                    </p>
                    <button onClick={() => setShowAddScreenshotModal(true)} className="btn btn-primary">
                      Add Screenshot
                    </button>
                  </div>
                ) : (
                  <div className="screenshots-list">
                    {screenshots.map((screenshot) => {
                      // Handle relative URLs for uploaded screenshots
                      const API_URL = process.env.REACT_APP_API_URL || '';
                      let imgSrc = screenshot.url;
                      if (screenshot.url.startsWith('/api/')) {
                        // Replace /api/ with the actual API base URL
                        imgSrc = API_URL + screenshot.url.substring(4); // Remove '/api' prefix, keep the rest
                      }
                      return (
                      <div key={screenshot.id} className="screenshot-card">
                        <img 
                          src={imgSrc} 
                          alt={screenshot.caption || 'Screenshot'} 
                          className="screenshot-thumb"
                          onClick={() => setLightboxImage({ src: imgSrc, caption: screenshot.caption })}
                          style={{ cursor: 'pointer' }}
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="150"%3E%3Crect fill="%23ddd" width="200" height="150"/%3E%3Ctext x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="14" fill="%23999"%3EImage not found%3C/text%3E%3C/svg%3E';
                          }}
                        />
                        <div className="screenshot-info">
                          <p className="screenshot-caption">{screenshot.caption || '(No caption)'}</p>
                          <p className="text-small text-muted">
                            {screenshot.url.startsWith('/') ? 'Uploaded' : (() => {
                              try {
                                return new URL(screenshot.url).hostname;
                              } catch {
                                return 'Screenshot';
                              }
                            })()}
                          </p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRemoveScreenshot(screenshot.id); }}
                          className="screenshot-delete-btn"
                          title="Remove screenshot"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                        </button>
                      </div>
                    );})}
                  </div>
                )}
              </div>

              {/* Videos Section */}
              <div className="media-section mt-4">
                <div className="d-flex justify-between align-center mb-3">
                  <h3>Videos ({videos.length})</h3>
                  <button onClick={() => setShowAddVideoModal(true)} className="btn btn-primary btn-sm">
                    + Add Video
                  </button>
                </div>
                
                {videos.length === 0 ? (
                  <div className="empty-state">
                    <h4>No videos yet</h4>
                    <p className="text-muted">
                      Add YouTube or custom videos to your project
                    </p>
                    <button onClick={() => setShowAddVideoModal(true)} className="btn btn-primary">
                      Add Video
                    </button>
                  </div>
                ) : (
                  <div className="videos-list">
                    {videos.map((video) => (
                      <div key={video.id} className="video-card">
                        <div className="video-header">
                          <span className="video-type-badge">{video.type === 'youtube' ? '▶ YouTube' : '🎬 Video'}</span>
                          <button
                            onClick={() => handleRemoveVideo(video.id)}
                            className="btn btn-danger btn-sm"
                          >
                            Remove
                          </button>
                        </div>
                        <p className="video-title">{video.title || '(No title)'}</p>
                        <p className="text-small text-muted video-url">{video.url}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Collaborators Tab */}
          {activeTab === 'collaborators' && (
            <div className="tab-pane">
              <div className="d-flex justify-between align-center mb-3">
                <h3>Collaborators</h3>
                <button
                  onClick={() => setShowCollabModal(true)}
                  className="btn btn-primary btn-sm"
                >
                  + Add Collaborator
                </button>
              </div>

              {collaborators.length === 0 ? (
                <div className="empty-state">
                  <h4>No collaborators yet</h4>
                  <p className="text-muted">
                    {project.is_public 
                      ? 'Add users to give them special access to download files.' 
                      : 'Add users to give them access to private files.'}
                  </p>
                  <button onClick={() => setShowCollabModal(true)} className="btn btn-primary">
                    Add Collaborator
                  </button>
                </div>
              ) : (
                <div className="collaborators-list">
                  {collaborators.map((username) => (
                    <div key={username} className="collaborator-item">
                      <span className="collaborator-name">{username}</span>
                      <button
                        onClick={() => handleRemoveCollaborator(username)}
                        className="btn btn-danger btn-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Updates Tab */}
          {activeTab === 'updates' && (
            <div className="tab-pane">
              <h3 className="mb-3">Project Updates</h3>
              <ProjectFeed 
                projectId={projectId} 
                isOwner={true} 
                username={user?.username}
              />
            </div>
          )}

          {/* Merchant Settings Tab */}
          {activeTab === 'settings' && (
            <div className="tab-pane">
              <h3 className="mb-3">Merchant Settings</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Created</span>
                  <span className="info-value">{formatDate(project.created_at)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Last Updated</span>
                  <span className="info-value">{formatDate(project.updated_at)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Project ID</span>
                  <code className="info-value">{project.project_id}</code>
                </div>
                <div className="info-item">
                  <span className="info-label">Slug</span>
                  <code className="info-value">{project.slug}</code>
                </div>
                <div className="info-item">
                  <span className="info-label">Visibility</span>
                  <span className={`badge ${project.is_public ? 'badge-success' : 'badge-warning'}`}>
                    {project.is_public ? 'Public' : 'Private'}
                  </span>
                </div>
              </div>

              <hr className="my-3" style={{borderColor: 'var(--border-color, #4a443a)'}} />

              <h4 className="mb-2">🔐 Crypto Wallets</h4>
              <p className="text-muted mb-2">Generate a wallet for each chain you want to accept. Each buyer gets a unique address.</p>
              
              {walletLoading ? (
                <p>Loading wallets...</p>
              ) : (
                <div className="wallets-grid">
                  {['btc', 'eth', 'usdt_bep20', 'usdt_trc20'].map((chain) => {
                    const wallet = wallets.find(w => w.chain === chain);
                    const chainNames = {btc: 'Bitcoin (BTC)', eth: 'Ethereum (ETH)', usdt_bep20: 'USDT BEP20 (BSC)', usdt_trc20: 'USDT TRC20 (TRON)'};
                    return (
                      <div key={chain} className="wallet-item" style={{padding: '0.75rem', border: '1px solid var(--border-color, #4a443a)', borderRadius: '4px', marginBottom: '0.5rem'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                          <strong>{chainNames[chain]}</strong>
                          {wallet ? (
                            <div style={{display: 'flex', gap: '0.5rem'}}>
                              {wallet.has_seed && (
                                <button className="btn btn-warning btn-sm" onClick={() => handleViewSeed(chain)}>
                                  🔑 View Seed
                                </button>
                              )}
                              <span className="badge badge-success">Active</span>
                            </div>
                          ) : (
                            <button className="btn btn-primary btn-sm" onClick={() => handleGenerateWallet(chain)}>
                              Generate
                            </button>
                          )}
                        </div>
                        {wallet && (
                          <code style={{fontSize: '0.8rem', wordBreak: 'break-all', display: 'block', marginTop: '0.5rem'}}>
                            {wallet.xpub.substring(0, 20)}...{wallet.xpub.substring(wallet.xpub.length - 10)}
                          </code>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Sales Dashboard - Owner Only */}
      {user && project && user.username === project.owner && project.pricing_type !== 'free' && (
        <div className="card mb-3">
          <h3 className="mb-3">💰 Sales Dashboard</h3>
          {salesLoading ? (
            <p>Loading sales...</p>
          ) : salesData ? (
            <>
              <div className="stats-bar mb-3">
                <div className="stat-item">
                  <span className="stat-value">{salesData.total_paid_count}</span>
                  <span className="stat-label">Paid Sales</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{salesData.total_pending_count}</span>
                  <span className="stat-label">Pending</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{salesData.total_revenue.toFixed(8)}</span>
                  <span className="stat-label">Total Crypto</span>
                </div>
              </div>

              {salesData.pending_payments.length > 0 && (
                <>
                  <h4 className="mb-2">Pending Payments</h4>
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Buyer</th>
                          <th>Amount</th>
                          <th>Chain</th>
                          <th>Tx Hash</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {salesData.pending_payments.map((p) => (
                          <tr key={p.id}>
                            <td>{p.backer_name}</td>
                            <td>{p.crypto_amount} {p.crypto_currency}</td>
                            <td>{p.payment_chain}</td>
                            <td><code style={{fontSize: '0.8rem'}}>{p.tx_hash}</code></td>
                            <td>
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={async () => {
                                  try {
                                    await projectsAPI.verifyPayment(projectId, p.id);
                                    setMessage('Payment verified!');
                                    loadSales();
                                  } catch (err) {
                                    setMessage(err.response?.data?.error || 'Failed to verify');
                                  }
                                }}
                              >
                                Verify
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {salesData.paid_backers.length > 0 && (
                <>
                  <h4 className="mb-2 mt-3">Completed Sales</h4>
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Buyer</th>
                          <th>Amount</th>
                          <th>Chain</th>
                          <th>Tx Hash</th>
                          <th>Paid At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {salesData.paid_backers.map((p) => (
                          <tr key={p.id}>
                            <td>{p.backer_name}</td>
                            <td>{p.crypto_amount} {p.crypto_currency}</td>
                            <td>{p.payment_chain}</td>
                            <td><code style={{fontSize: '0.8rem'}}>{p.tx_hash}</code></td>
                            <td>{p.paid_at ? new Date(p.paid_at).toLocaleString() : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {salesData.pending_payments.length === 0 && salesData.paid_backers.length === 0 && (
                <p className="text-muted">No sales yet.</p>
              )}
            </>
          ) : (
            <p className="text-muted">Could not load sales data.</p>
          )}
        </div>
      )}

      {/* Add Collaborator Modal */}
      {showCollabModal && (
        <div className="modal-overlay" onClick={() => setShowCollabModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Collaborator</h3>
              <button
                className="modal-close"
                onClick={() => setShowCollabModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleAddCollaborator}>
              <div className="form-group">
                <label className="form-label">Username or Email</label>
                <input
                  type="text"
                  className="form-control"
                  value={collabInput}
                  onChange={(e) => setCollabInput(e.target.value)}
                  placeholder="Enter username or email"
                  required
                  disabled={collabLoading}
                />
                <small className="text-muted">
                  Collaborators can view and download all project files
                </small>
              </div>
              <div className="d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={collabLoading}
                >
                  {collabLoading ? 'Adding...' : 'Add'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCollabModal(false)}
                  disabled={collabLoading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Files Modal */}
      {showAddFileModal && (
        <div className="modal-overlay" onClick={() => setShowAddFileModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Link Files to Project</h2>
              <button
                className="modal-close"
                onClick={() => setShowAddFileModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {filesLoading ? (
                <div className="loading">
                  <div className="spinner"></div>
                  <p>Loading your files...</p>
                </div>
              ) : myFiles.length === 0 ? (
                <div className="empty-state">
                  <h4>No available files</h4>
                  <p className="text-muted">
                    All your uploaded files are already linked to this project, or you haven't uploaded any files yet.
                  </p>
                  <Link 
                    to="/my-files" 
                    className="btn btn-primary mt-2"
                    onClick={() => setShowAddFileModal(false)}
                  >
                    Upload New Files
                  </Link>
                </div>
              ) : (
                <>
                  <p className="text-muted mb-3">
                    Select files from your uploads to link to this project. 
                    Selected: {selectedFiles.length}
                  </p>
                  <div className="files-list">
                    {myFiles.map((file) => (
                      <div 
                        key={file.file_id}
                        className={`file-select-item ${selectedFiles.includes(file.file_id) ? 'selected' : ''}`}
                        onClick={() => toggleFileSelection(file.file_id)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedFiles.includes(file.file_id)}
                          onChange={() => toggleFileSelection(file.file_id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="file-select-info">
                          <div className="file-select-name">{file.filename}</div>
                          <div className="file-select-meta">
                            <span>{formatBytes(file.size)}</span>
                            <span className="mx-2">•</span>
                            <span>{file.is_public ? 'Public' : 'Private'}</span>
                            <span className="mx-2">•</span>
                            <span>{formatDate(file.uploaded_at)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            {myFiles.length > 0 && (
              <div className="modal-footer">
                <button
                  onClick={() => setShowAddFileModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddSelectedFiles}
                  className="btn btn-primary"
                  disabled={selectedFiles.length === 0 || filesLoading}
                >
                  Add {selectedFiles.length > 0 ? `${selectedFiles.length} ` : ''}File{selectedFiles.length !== 1 ? 's' : ''}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Screenshot Modal */}
      {showAddScreenshotModal && (
        <div className="modal-overlay" onClick={resetScreenshotModal}>
          <div 
            className="modal-content modal-lg" 
            onClick={(e) => e.stopPropagation()}
            onPaste={handleScreenshotPaste}
            onDrop={handleScreenshotDrop}
            onDragOver={handleDragOver}
          >
            <div className="modal-header">
              <h2>Add Screenshot</h2>
              <button
                className="modal-close"
                onClick={resetScreenshotModal}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleAddScreenshot}>
                {/* Upload Mode Toggle */}
                <div className="upload-mode-toggle mb-3">
                  <button
                    type="button"
                    className={`toggle-btn ${uploadMode === 'file' ? 'active' : ''}`}
                    onClick={() => setUploadMode('file')}
                  >
                    📤 Upload / Paste
                  </button>
                  <button
                    type="button"
                    className={`toggle-btn ${uploadMode === 'url' ? 'active' : ''}`}
                    onClick={() => setUploadMode('url')}
                  >
                    🔗 Use URL
                  </button>
                </div>

                {uploadMode === 'file' ? (
                  <div className="form-group">
                    <label className="form-label">Screenshot File *</label>
                    <div 
                      className={`file-upload-area ${screenshotData.file ? 'has-file' : ''}`}
                      onDrop={handleScreenshotDrop}
                      onDragOver={handleDragOver}
                    >
                      <input
                        type="file"
                        id="screenshot-file"
                        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                        onChange={handleScreenshotFileChange}
                        disabled={mediaLoading}
                        className="file-input-hidden"
                      />
                      <label htmlFor="screenshot-file" className="file-upload-label">
                        {screenshotData.file ? (
                          <span className="file-selected">
                            ✅ {screenshotData.file.name}
                          </span>
                        ) : (
                          <div className="file-placeholder">
                            <span className="upload-icon">📷</span>
                            <span className="upload-text">Click to select, drag & drop, or paste (Ctrl+V)</span>
                            <span className="upload-hint">PNG, JPG, GIF, or WebP (max 10MB)</span>
                          </div>
                        )}
                      </label>
                    </div>
                    <small className="text-muted paste-hint">
                      💡 Tip: Press <kbd>Ctrl</kbd>+<kbd>V</kbd> (or <kbd>⌘</kbd>+<kbd>V</kbd>) to paste a screenshot from clipboard
                    </small>
                  </div>
                ) : (
                  <div className="form-group">
                    <label className="form-label">Screenshot URL *</label>
                    <input
                      type="url"
                      className="form-control"
                      placeholder="https://example.com/screenshot.png"
                      value={screenshotData.url}
                      onChange={(e) => setScreenshotData({ ...screenshotData, url: e.target.value })}
                      disabled={mediaLoading}
                    />
                    <small className="text-muted">Direct link to image (PNG, JPG, WebP)</small>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Caption</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Optional caption (e.g., 'Main dashboard')"
                    value={screenshotData.caption}
                    onChange={(e) => setScreenshotData({ ...screenshotData, caption: e.target.value })}
                    maxLength={100}
                    disabled={mediaLoading}
                  />
                </div>

                {/* Preview */}
                {(screenshotPreview || screenshotData.url) && (
                  <div className="form-group">
                    <label className="form-label">Preview</label>
                    <div className="screenshot-preview">
                      <img 
                        src={screenshotPreview || screenshotData.url} 
                        alt="Preview" 
                        className="preview-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                )}

                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={resetScreenshotModal}
                    className="btn btn-secondary"
                    disabled={mediaLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={mediaLoading || (uploadMode === 'file' && !screenshotData.file) || (uploadMode === 'url' && !screenshotData.url)}
                  >
                    {mediaLoading ? 'Uploading...' : 'Add Screenshot'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Video Modal */}
      {showAddVideoModal && (
        <div className="modal-overlay" onClick={() => setShowAddVideoModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Video</h2>
              <button
                className="modal-close"
                onClick={() => setShowAddVideoModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleAddVideo}>
                <div className="form-group">
                  <label className="form-label">Video Type</label>
                  <select
                    className="form-control"
                    value={videoData.type}
                    onChange={(e) => setVideoData({ ...videoData, type: e.target.value })}
                    disabled={mediaLoading}
                  >
                    <option value="youtube">YouTube</option>
                    <option value="custom">Custom Video File</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Video URL *</label>
                  <input
                    type="url"
                    className="form-control"
                    placeholder={videoData.type === 'youtube' ? 'https://www.youtube.com/watch?v=...' : 'https://example.com/video.mp4'}
                    value={videoData.url}
                    onChange={(e) => setVideoData({ ...videoData, url: e.target.value })}
                    required
                    disabled={mediaLoading}
                  />
                  <small className="text-muted">
                    {videoData.type === 'youtube' 
                      ? 'YouTube video URL (any format)' 
                      : 'Direct link to video file (MP4, WebM, etc.)'}
                  </small>
                </div>

                <div className="form-group">
                  <label className="form-label">Video Title</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Optional title (e.g., 'Product Demo')"
                    value={videoData.title}
                    onChange={(e) => setVideoData({ ...videoData, title: e.target.value })}
                    maxLength={100}
                    disabled={mediaLoading}
                  />
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={() => setShowAddVideoModal(false)}
                    className="btn btn-secondary"
                    disabled={mediaLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={mediaLoading}
                  >
                    {mediaLoading ? 'Adding...' : 'Add Video'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="lightbox-overlay" onClick={() => setLightboxImage(null)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightboxImage(null)}>×</button>
            <img src={lightboxImage.src} alt={lightboxImage.caption || 'Screenshot'} className="lightbox-image" />
            {lightboxImage.caption && <p className="lightbox-caption">{lightboxImage.caption}</p>}
          </div>
        </div>
      )}

      {/* Transfer Ownership Modal */}
      {showTransferModal && (
        <div className="modal-overlay" onClick={() => setShowTransferModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Transfer Project Ownership</h2>
            <p className="text-muted mb-3">
              ⚠️ Warning: This action cannot be undone. You will become a collaborator after the transfer.
            </p>
            <form onSubmit={handleTransferOwnership}>
              <div className="form-group mb-3">
                <label className="form-label">New Owner Username</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter username"
                  value={transferUsername}
                  onChange={(e) => setTransferUsername(e.target.value)}
                  disabled={transferLoading}
                  required
                />
                <small className="text-muted">
                  The user must have an account on SharePanda
                </small>
              </div>
              <div className="d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-warning"
                  disabled={transferLoading || !transferUsername.trim()}
                >
                  {transferLoading ? 'Transferring...' : 'Transfer Ownership'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowTransferModal(false);
                    setTransferUsername('');
                  }}
                  disabled={transferLoading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Seed Phrase Backup Modal */}
      {showSeedModal && (
        <div className="modal-overlay" onClick={() => setShowSeedModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🔐 BACK UP YOUR SEED PHRASE</h3>
              <button className="modal-close" onClick={() => setShowSeedModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="alert alert-warning">
                This seed phrase controls your wallet. If you lose it, you cannot recover funds. We do not store it.
              </div>
              <div className="form-group">
                <label>Your Seed Phrase (12 words)</label>
                <textarea
                  className="form-control"
                  value={seedPhrase}
                  readOnly
                  rows={2}
                  style={{fontFamily: 'monospace', fontSize: '1.1rem'}}
                />
              </div>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  navigator.clipboard.writeText(seedPhrase);
                  alert('Seed phrase copied to clipboard!');
                }}
              >
                Copy to Clipboard
              </button>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => { setShowSeedModal(false); setSeedPhrase(''); }}>
                I Have Backed It Up
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectDetail;
